import axios from 'axios';
import { ADMIN_API_BASE } from '../lib/config';

export const ADMIN_API = ADMIN_API_BASE;

export const adminApi = axios.create({ baseURL: ADMIN_API_BASE, timeout: 25000 });

// Attach Bearer token from localStorage on every request
adminApi.interceptors.request.use((config) => {
    const token = localStorage.getItem('admin_token');
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
});

// Auto-redirect to login on 401
adminApi.interceptors.response.use(
    (r) => r,
    (err) => {
        if (err.response?.status === 401 && typeof window !== 'undefined') {
            localStorage.removeItem('admin_token');
            if (!window.location.pathname.endsWith('/admin/login')) {
                window.location.href = '/admin/login';
            }
        }
        return Promise.reject(err);
    }
);

// Endpoints
export const adminLogin = (email, password) => adminApi.post('/auth/login', { email, password }).then(r => r.data);
export const adminMe = () => adminApi.get('/auth/me').then(r => r.data);
export const adminDashboard = () => adminApi.get('/dashboard').then(r => r.data);
export const adminLookups = () => adminApi.get('/lookups').then(r => r.data);

export const adminGet = (path, params) => adminApi.get(path, { params }).then(r => r.data);
export const adminPost = (path, payload) => adminApi.post(path, payload).then(r => r.data);
export const adminPut = (path, payload) => adminApi.put(path, payload).then(r => r.data);
export const adminDelete = (path) => adminApi.delete(path).then(r => r.data);

// Properties
export const adminListProperties = (params) => adminApi.get('/properties', { params }).then(r => r.data);
export const adminCreateProperty = (payload) => adminApi.post('/properties', payload).then(r => r.data);
export const adminBulkCreateProperties = (items) =>
    adminApi.post('/properties/bulk', { items }, { timeout: 120000 }).then(r => r.data);
export const adminListImportReports = (params) =>
    adminApi.get('/import-reports', { params }).then(r => r.data);
export const adminGetImportReport = (id) =>
    adminApi.get(`/import-reports/${id}`).then(r => r.data);
export const adminDeleteImportReport = (id) =>
    adminApi.delete(`/import-reports/${id}`).then(r => r.data);
export const adminUpdateProperty = (id, payload) => adminApi.put(`/properties/${id}`, payload).then(r => r.data);
export const adminDeleteProperty = (id) => adminApi.delete(`/properties/${id}`).then(r => r.data);

// Leads
export const adminListLeads = (params) => adminApi.get('/leads', { params }).then(r => r.data);
export const adminGetLead = (id) => adminApi.get(`/leads/${id}`).then(r => r.data);
export const adminUpdateLead = (id, payload) => adminApi.put(`/leads/${id}`, payload).then(r => r.data);
export const adminDeleteLead = (id) => adminApi.delete(`/leads/${id}`).then(r => r.data);

// Banners
export const adminListBanners = () => adminApi.get('/banners').then(r => r.data);
export const adminCreateBanner = (payload) => adminApi.post('/banners', payload).then(r => r.data);
export const adminUpdateBanner = (id, payload) => adminApi.put(`/banners/${id}`, payload).then(r => r.data);
export const adminDeleteBanner = (id) => adminApi.delete(`/banners/${id}`).then(r => r.data);

// Blogs
export const adminListBlogs = () => adminApi.get('/blogs').then(r => r.data);
export const adminCreateBlog = (payload) => adminApi.post('/blogs', payload).then(r => r.data);
export const adminUpdateBlog = (id, payload) => adminApi.put(`/blogs/${id}`, payload).then(r => r.data);
export const adminDeleteBlog = (id) => adminApi.delete(`/blogs/${id}`).then(r => r.data);

// Testimonials
export const adminListTestimonials = () => adminApi.get('/testimonials').then(r => r.data);
export const adminCreateTestimonial = (payload) => adminApi.post('/testimonials', payload).then(r => r.data);
export const adminUpdateTestimonial = (id, payload) => adminApi.put(`/testimonials/${id}`, payload).then(r => r.data);
export const adminDeleteTestimonial = (id) => adminApi.delete(`/testimonials/${id}`).then(r => r.data);

// Cities
export const adminListCities = () => adminApi.get('/cities').then(r => r.data);
export const adminCreateCity = (p) => adminApi.post('/cities', p).then(r => r.data);
export const adminUpdateCity = (id, p) => adminApi.put(`/cities/${id}`, p).then(r => r.data);
export const adminDeleteCity = (id) => adminApi.delete(`/cities/${id}`).then(r => r.data);

// Localities
export const adminListLocalities = (params) => adminApi.get('/localities', { params }).then(r => r.data);
export const adminCreateLocality = (p) => adminApi.post('/localities', p).then(r => r.data);
export const adminUpdateLocality = (id, p) => adminApi.put(`/localities/${id}`, p).then(r => r.data);
export const adminDeleteLocality = (id) => adminApi.delete(`/localities/${id}`).then(r => r.data);

// Developers
export const adminListDevelopers = () => adminApi.get('/developers').then(r => r.data);
export const adminCreateDeveloper = (p) => adminApi.post('/developers', p).then(r => r.data);
export const adminUpdateDeveloper = (id, p) => adminApi.put(`/developers/${id}`, p).then(r => r.data);
export const adminDeleteDeveloper = (id) => adminApi.delete(`/developers/${id}`).then(r => r.data);

// Page Sections
export const adminListSections = (page_key) => adminApi.get('/page-sections', { params: { page_key } }).then(r => r.data);
export const adminCreateSection = (p) => adminApi.post('/page-sections', p).then(r => r.data);
export const adminUpdateSection = (id, p) => adminApi.put(`/page-sections/${id}`, p).then(r => r.data);
export const adminDeleteSection = (id) => adminApi.delete(`/page-sections/${id}`).then(r => r.data);

// FAQs
export const adminListFaqs = (page_key) => adminApi.get('/faqs', { params: { page_key } }).then(r => r.data);
export const adminCreateFaq = (p) => adminApi.post('/faqs', p).then(r => r.data);
export const adminUpdateFaq = (id, p) => adminApi.put(`/faqs/${id}`, p).then(r => r.data);
export const adminDeleteFaq = (id) => adminApi.delete(`/faqs/${id}`).then(r => r.data);

// Page SEO
export const adminGetPageSeo = (page_key) => adminApi.get(`/page-seo/${page_key}`).then(r => r.data);
export const adminSavePageSeo = (page_key, p) => adminApi.put(`/page-seo/${page_key}`, p).then(r => r.data);

// Site Settings
export const adminListSettings = () => adminApi.get('/site-settings').then(r => r.data);
export const adminSaveSettings = (p) => adminApi.put('/site-settings', p).then(r => r.data);

// Uploads (S3 presign + delete)
export const adminPresignUploads = (files) => adminApi.post('/uploads/presign', { files }).then(r => r.data);
export const adminDeleteUploads = (payload) => adminApi.post('/uploads/delete', payload).then(r => r.data);
export const adminUploadConfig = () => adminApi.get('/uploads/config').then(r => r.data);

// Users (Brokers / Resellers / Owners)
export const adminListUsers = (params) => adminApi.get('/users', { params }).then(r => r.data);
export const adminGetUser = (id) => adminApi.get(`/users/${id}`).then(r => r.data);
export const adminApproveUser = (id, note = '') => adminApi.post(`/users/${id}/approve`, { note }).then(r => r.data);
export const adminRejectUser = (id, reason) => adminApi.post(`/users/${id}/reject`, { reason }).then(r => r.data);
export const adminSuspendUser = (id) => adminApi.post(`/users/${id}/suspend`).then(r => r.data);
export const adminReactivateUser = (id) => adminApi.post(`/users/${id}/reactivate`).then(r => r.data);
export const adminDeleteUser = (id) => adminApi.delete(`/users/${id}`).then(r => r.data);

export function logout() {
    localStorage.removeItem('admin_token');
    window.location.href = '/admin/login';
}

export function isLoggedIn() {
    return !!localStorage.getItem('admin_token');
}
